import { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useInvoicePayments } from '../api/use-invoice-payments';
import { DeletePaymentDialog } from '../dialogs/delete-payment-dialog';
import { formatAmount } from '../lib/format-amount';
import { invoiceActionAvailable } from '../lib/invoice-actions';
import { canonicalInvoiceStatus } from '../lib/invoice-status';
import { isRaiseMarker } from '../lib/raise-marker';
import type { InvoiceRow, PaymentRow } from '../types';

export const PAYMENT_ELIGIBLE_STATUSES: ReadonlySet<string> = new Set([
  'Issued',
  'Partially Paid',
  'Paid',
]);

// A payment can be recorded (or deleted) only on an eligible invoice. The
// eligible set (Issued / Partially Paid / Paid) already excludes Void.
// Availability comes from the shared action model so the row and drawer
// cannot disagree.
export function canRecordPayments(invoice: InvoiceRow): boolean {
  return invoiceActionAvailable(invoice, 'record_payment');
}

function canRender(invoice: InvoiceRow): boolean {
  const status = canonicalInvoiceStatus(invoice.status);
  if (status === null) return false;
  return PAYMENT_ELIGIBLE_STATUSES.has(status);
}

export interface InlinePaymentsListProps {
  readonly invoice: InvoiceRow;
}

interface RowProps {
  readonly invoice: InvoiceRow;
  readonly payment: PaymentRow;
  readonly canDelete: boolean;
  readonly onDelete: (payment: PaymentRow) => void;
}

function PaymentLine({ invoice, payment, canDelete, onDelete }: RowProps) {
  const reference = payment.transfer_ref ?? payment.airwallex_transfer_id ?? payment.bank_reference;
  return (
    <li
      className="border-border/60 flex flex-wrap items-center justify-between gap-2 border-t py-1.5 text-xs"
      data-testid={`invoice-${invoice.id}-payment-${payment.id}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-foreground font-medium">{payment.paid_date ?? '—'}</span>
        <span className="text-muted-foreground">
          {formatAmount(payment.total_amount_paid, payment.currency)}
        </span>
        {reference !== null && reference !== '' && (
          <span className="text-muted-foreground">Ref: {reference}</span>
        )}
        {payment.reference_note !== null && payment.reference_note !== '' && (
          <span className="text-muted-foreground italic">{payment.reference_note}</span>
        )}
      </div>
      {canDelete && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Delete payment"
          data-testid={`invoice-${invoice.id}-payment-${payment.id}-delete`}
          onClick={() => {
            onDelete(payment);
          }}
        >
          <Trash2 className="size-3" />
        </Button>
      )}
    </li>
  );
}

function ToggleButton({
  invoice,
  expanded,
  onClick,
}: Readonly<{
  invoice: InvoiceRow;
  expanded: boolean;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
      aria-expanded={expanded}
      onClick={onClick}
      data-testid={`invoice-${invoice.id}-payments-toggle`}
    >
      {expanded ? (
        <ChevronDown className="size-3" aria-hidden />
      ) : (
        <ChevronRight className="size-3" aria-hidden />
      )}
      Payments
    </button>
  );
}

function PaymentsList({
  invoice,
  payments,
  isLoading,
  isVoid,
  onDelete,
}: Readonly<{
  invoice: InvoiceRow;
  payments: readonly PaymentRow[];
  isLoading: boolean;
  isVoid: boolean;
  onDelete: (payment: PaymentRow) => void;
}>) {
  return (
    <ul className="mt-1" data-testid={`invoice-${invoice.id}-payments`}>
      {isLoading && <li className="text-muted-foreground py-1 text-xs">Loading…</li>}
      {!isLoading && payments.length === 0 && (
        <li className="text-muted-foreground py-1 text-xs">No payments yet.</li>
      )}
      {payments.map((p) => (
        <PaymentLine
          key={p.id}
          invoice={invoice}
          payment={p}
          // The raise marker reverses via Unmark, not delete; tranche rows stay deletable.
          canDelete={!isVoid && p.deleted_at === null && !isRaiseMarker(p)}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
}

export function InlinePaymentsList({ invoice }: InlinePaymentsListProps) {
  if (!canRender(invoice)) return null;
  return <InlinePaymentsListInner invoice={invoice} />;
}

function InlinePaymentsListInner({ invoice }: InlinePaymentsListProps) {
  const [expanded, setExpanded] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PaymentRow | null>(null);
  const isVoid = invoice.status === 'Void';
  const query = useInvoicePayments(invoice.id, expanded);
  const payments = query.data ?? [];

  return (
    <div className="w-full">
      <ToggleButton
        invoice={invoice}
        expanded={expanded}
        onClick={() => {
          setExpanded((v) => !v);
        }}
      />
      {expanded && (
        <PaymentsList
          invoice={invoice}
          payments={payments}
          isLoading={query.isLoading}
          isVoid={isVoid}
          onDelete={setDeleteTarget}
        />
      )}
      {deleteTarget !== null && (
        <DeletePaymentDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          invoice={invoice}
          payment={deleteTarget}
          onDeleted={() => {
            setDeleteTarget(null);
          }}
        />
      )}
    </div>
  );
}
